import { useEffect, useState } from "react";
import { read } from "../../net/rally.ts";

export type LinkPreview = {
  url: string;
  host: string;
  title: string;
  description: string | null;
  favicon: string | null;
  image: string | null;
};

const held = new Map<string, LinkPreview | null>();
const asking = new Map<string, Promise<LinkPreview | null>>();

function keyOf(conversation: string, url: string): string {
  return `${conversation}\0${url}`;
}

async function ask(conversation: string, url: string): Promise<LinkPreview | null> {
  const key = keyOf(conversation, url);
  if (held.has(key)) return held.get(key) ?? null;
  const underway = asking.get(key);
  if (underway) return underway;
  const request = read<LinkPreview | null>("link.preview", { conversation, url })
    .catch(() => null)
    .then((preview) => {
      held.set(key, preview);
      asking.delete(key);
      return preview;
    });
  asking.set(key, request);
  return request;
}

export function useLinkPreview(
  conversation: string | undefined,
  url: string,
): LinkPreview | null | undefined {
  const key = conversation ? keyOf(conversation, url) : "";
  const [preview, setPreview] = useState<{ key: string; value: LinkPreview | null | undefined }>(() => ({
    key,
    value: key && held.has(key) ? (held.get(key) ?? null) : undefined,
  }));
  useEffect(() => {
    if (!conversation) return;
    let alive = true;
    void ask(conversation, url).then((found) => {
      if (alive) setPreview({ key, value: found });
    });
    return () => {
      alive = false;
    };
  }, [conversation, key, url]);
  return preview.key === key ? preview.value : undefined;
}
